import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import type { RecordedOperation } from '@/api/models';
import { PATH } from '@/router/PATH';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type RecordedNoticeProps = {
    recorded: RecordedOperation;
    onDismiss: () => void;
};

/**
 * The confirmation for an operation that has just posted.
 *
 * **A banner rather than a toast**, and that is a considered substitution. The
 * task asked for a toast; this app has no toast host mounted anywhere, and
 * introducing one — a provider, a region, a placement, a dismissal policy — is a
 * layout-wide change smuggled into a page task. The periods screen already
 * confirms a year-end close exactly this way, and its reasons apply harder here:
 * the confirmation carries a **link into the Journal**, and a link the user has
 * four seconds to click is a link they will miss.
 *
 * It names the entry by its **gapless number**, not by its uuid, because that is
 * what the Journal page and any conversation about it will call the entry.
 *
 * **A replay says so.** The idempotency key means a retried submission returns
 * the original entry and posts nothing; reporting that as a fresh "recorded"
 * would tell someone they had booked two expenses when they had booked one.
 */
export const RecordedNotice: React.FC<RecordedNoticeProps> = ({
    recorded,
    onDismiss,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex flex-wrap items-center gap-3 rounded-2xl bg-success-soft px-4 py-3 text-sm text-success-soft-foreground"
            {...getNodeTestIdProp('operations-record-result')}
        >
            <Icon aria-hidden icon="gravity-ui:circle-check" />
            <span {...getNodeTestIdProp('operations-record-result-message')}>
                {recorded.replayed
                    ? t('operations.record.result.replayed', {
                          amount: formatGel(recorded.totalDebitTetri),
                      })
                    : t('operations.record.result.posted', {
                          amount: formatGel(recorded.totalDebitTetri),
                      })}
            </span>
            <Link
                className="font-medium underline underline-offset-2"
                // The Journal **tab**, scoped to the entry's own date on both
                // sides. That tab takes its window from the Books shell and
                // cannot widen it, and a backdated recording is routinely
                // outside the shell's default month — so the range is passed
                // explicitly rather than left to a default that would land the
                // user on a list the entry is not in.
                to={PATH.ACCOUNTING.BOOKS.BIND_JOURNAL_FOR_ENTRY(
                    recorded.entryId,
                    { from: recorded.date, to: recorded.date },
                )}
                {...getNodeTestIdProp('operations-record-result-link')}
            >
                {recorded.entryNumber === null
                    ? t('operations.record.result.viewEntry')
                    : t('operations.record.result.entryNumber', {
                          number: recorded.entryNumber,
                      })}
            </Link>
            <Button
                aria-label={t('operations.record.result.dismiss')}
                className="ml-auto"
                isIconOnly
                onPress={onDismiss}
                size="sm"
                variant="ghost"
                {...getNodeTestIdProp('operations-record-result-dismiss')}
            >
                <Icon aria-hidden icon="gravity-ui:xmark" />
            </Button>
        </div>
    );
};
